export type RichPayload = Record<string, unknown>;

export type HRSummaryRichPayload = {
  avg_hr_bpm?: number;
  min_hr_bpm?: number;
  max_hr_bpm?: number;
  resting_hr?: number;
  avg_hrv_rmssd?: number;
  avg_hrv_sdnn?: number;
  timeline?: Array<Record<string, unknown>>;
  chart_data?: Array<Record<string, unknown>>;
  heart_rate_summary?: {
    avg_hr_bpm?: number;
    min_hr_bpm?: number;
    max_hr_bpm?: number;
    resting_hr_bpm?: number;
    avg_hrv_rmssd?: number;
    avg_hrv_sdnn?: number;
  } | null;
};

export const normalizeHrTimeline = (
  timeline: unknown
): Array<{ timestamp: string; value: number }> | undefined => {
  if (!Array.isArray(timeline)) return undefined;

  const normalized = timeline
    .map((point) => {
      if (!point || typeof point !== 'object') return null;
      const raw = point as Record<string, unknown>;
      const timestamp =
        typeof raw.timestamp === 'string'
          ? raw.timestamp
          : typeof raw.time === 'string'
            ? raw.time
            : '';
      const valueCandidate =
        typeof raw.value === 'number'
          ? raw.value
          : typeof raw.hr === 'number'
            ? raw.hr
            : typeof raw.bpm === 'number'
              ? raw.bpm
              : undefined;
      if (!timestamp || typeof valueCandidate !== 'number' || !Number.isFinite(valueCandidate)) {
        return null;
      }
      return { timestamp, value: valueCandidate };
    })
    .filter((point): point is { timestamp: string; value: number } => point !== null);

  return normalized.length > 0 ? normalized : undefined;
};

export type WorkoutSummaryRichPayload = {
  workout_type?: string;
  duration_min?: number;
  steps?: number;
  calories?: number;
  distance_meters?: number;
  source?: string;
};

export const parseWorkoutSummaryPayload = (payload: RichPayload): WorkoutSummaryRichPayload => ({
  workout_type: typeof payload.workout_type === 'string' ? payload.workout_type : undefined,
  duration_min: typeof payload.duration_min === 'number' ? payload.duration_min : undefined,
  steps: typeof payload.steps === 'number' ? payload.steps : undefined,
  calories: typeof payload.calories === 'number' ? payload.calories : undefined,
  distance_meters:
    typeof payload.distance_meters === 'number' ? payload.distance_meters : undefined,
  source: typeof payload.source === 'string' ? payload.source : undefined,
});

export type SleepSummaryRichPayload = {
  duration_h?: number;
  quality_score?: number;
  efficiency?: number;
  stages?: Record<string, unknown> | null;
  sleep_time?: string;
  awake_time?: string;
  sleep_date?: string;
  history?: Array<{
    sleep_time?: string;
    wake_time?: string;
  }>;
  temperature_delta?: number;
  heart_rate_summary?: {
    avg_hr_bpm?: number;
    resting_hr_bpm?: number;
    min_hr_bpm?: number;
    max_hr_bpm?: number;
    avg_hrv_rmssd?: number;
    avg_hrv_sdnn?: number;
  } | null;
  avg_hrv_rmssd?: number;
  avg_hrv_sdnn?: number;
  resting_hr?: number;
  avg_hr_bpm?: number;
  min_hr_bpm?: number;
  max_hr_bpm?: number;
  suggested_sleep?: Record<string, unknown>;
  ideal_sleep_time_tonight?: string;
  sleep_source?: string;
};

export const CONTENT_ICON: Record<string, string> = {
  workout_plan: '💪',
  workout_summary: '🏃',
  checklist: '✅',
  meal_suggestion: '🍽',
  meal_content: '🍽',
  find_location: '📍',
  transit_route: '🚇',
  journal_prompts: '📓',
  audio_player: '🎵',
  energy_summary: '⚡',
  energy: '⚡',
  sleep_summary: '🌙',
  sleep: '🌙',
  hr_summary: '❤️',
  hr: '❤️',
  place: '📍',
  metric_bar: '📊',
  meal_plan_week: '📅',
};

export const ZONE_COLOR: Record<string, string> = {
  peak: 'rgba(120,226,180,0.85)',
  calm: 'rgba(134,89,255,0.85)',
  low: 'rgba(232,115,148,0.85)',
  recovering: 'rgba(255,195,80,0.85)',
};

export const normalizeRichContentType = (type: string | undefined): string => {
  const normalized = typeof type === 'string' ? type.trim().toLowerCase() : '';
  if (!normalized) return '';
  if (normalized === 'place' || normalized === 'findlocation') return 'find_location';
  if (normalized === 'hr' || normalized === 'hrsummary') return 'hr_summary';
  if (normalized === 'sleep' || normalized === 'sleepsummary') return 'sleep_summary';
  if (normalized === 'energy_summary' || normalized === 'energysummary') return 'energy';
  return normalized;
};

export const formatDistance = (distanceMeters?: number, walkMin?: number): string => {
  const parts: string[] = [];
  if (typeof distanceMeters === 'number') {
    parts.push(
      distanceMeters >= 1000
        ? `${(distanceMeters / 1000).toFixed(1)} km`
        : `${Math.round(distanceMeters)} m`
    );
  }
  if (typeof walkMin === 'number') {
    parts.push(`${Math.round(walkMin)} min walk`);
  }
  return parts.join(' · ');
};

export const isFindLocationLike = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  return typeof p.query === 'string' || Array.isArray(p.nearby_places);
};

export const isHRSummaryLike = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  const heartRateSummary =
    p.heart_rate_summary && typeof p.heart_rate_summary === 'object'
      ? (p.heart_rate_summary as Record<string, unknown>)
      : null;
  return (
    typeof p.avg_hr_bpm === 'number' ||
    typeof p.min_hr_bpm === 'number' ||
    typeof p.max_hr_bpm === 'number' ||
    typeof p.resting_hr === 'number' ||
    typeof p.avg_hrv_rmssd === 'number' ||
    typeof heartRateSummary?.avg_hr_bpm === 'number' ||
    Array.isArray(p.timeline) ||
    Array.isArray(p.chart_data)
  );
};

export const isSleepSummaryLike = (payload: unknown): payload is SleepSummaryRichPayload => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  return (
    typeof p.duration_h === 'number' ||
    typeof p.deep_h === 'number' ||
    typeof p.rem_h === 'number' ||
    typeof p.quality_score === 'number'
  );
};

export const toPositiveNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
};

export const formatSleepScore = (score?: number): string | null => {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  const percentage = score <= 1 ? score * 100 : score;
  return `${percentage.toFixed(1)}%`;
};

export const toTotalSleepHours = (durationHours?: number): { hours: number; minutes: number } => {
  if (typeof durationHours !== 'number' || !Number.isFinite(durationHours) || durationHours <= 0) {
    return { hours: 0, minutes: 0 };
  }
  const totalMinutes = Math.round(durationHours * 60);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
};

export type SleepStage = {
  deep: number;
  light: number;
  rem: number;
  awake: number;
};

export const normalizeSleepStages = (rawStages: unknown): SleepStage | undefined => {
  if (!rawStages || typeof rawStages !== 'object' || Array.isArray(rawStages)) {
    return undefined;
  }

  const stages = rawStages as Record<string, unknown>;
  const normalized: SleepStage = { deep: 0, light: 0, rem: 0, awake: 0 };

  const toStageSeconds = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return 0;
    const v = value as Record<string, unknown>;
    const minuteCandidate =
      typeof v.duration_min === 'number'
        ? v.duration_min
        : typeof v.durationMin === 'number'
          ? v.durationMin
          : typeof v.minutes === 'number'
            ? v.minutes
            : typeof v.min === 'number'
              ? v.min
              : undefined;
    if (
      typeof minuteCandidate === 'number' &&
      Number.isFinite(minuteCandidate) &&
      minuteCandidate > 0
    ) {
      return minuteCandidate > 24 * 60
        ? Math.round(minuteCandidate)
        : Math.round(minuteCandidate * 60);
    }
    return 0;
  };

  normalized.deep = toStageSeconds(stages.deep ?? stages.deep_sleep ?? stages.deepSleep);
  normalized.light = toStageSeconds(
    stages.light ?? stages.light_sleep ?? stages.lightSleep ?? stages.core
  );
  normalized.rem = toStageSeconds(stages.rem ?? stages.rem_sleep ?? stages.remSleep);
  normalized.awake = toStageSeconds(stages.awake ?? stages.wake);

  const hasData = normalized.deep > 0 || normalized.light > 0 || normalized.rem > 0;
  return hasData ? normalized : undefined;
};
